/**
 * ForderungsCockpitPage — morgendliche Fall-Prüfungs-Ansicht des Forderungsmanagers.
 *
 * Großes Fenster: links priorisierte Fallliste, rechts Detailansicht mit
 * Zusammenfassung, chronologischem Verlauf, Dokumenten, Antwort-Composer und
 * Entscheidungs-Buttons.
 *
 * Datenquellen (nur lesend / RPC):
 *  - View  v_forderung_cockpit   (eine Zeile je Forderung)
 *  - View  v_forderung_verlauf   (chronologisch, mehrere Zeilen je ak)
 *  - Tab.  dokumente             (Dateien im Storage-Bucket "dokumente")
 * Aktionen:
 *  - RPC   fn_forderung_set_status
 *  - RPC   fn_forderung_reply_draft
 *
 * Konventionen: kein Layout-Wrapper (DashboardLayout kommt von App.tsx),
 * supabase aus '@/lib/supabase', toast aus 'sonner', nur lucide-react Icons,
 * Tailwind-Utilities, Akzentfarbe #9B1C1C.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  Inbox, RefreshCw, Search, Loader2, AlertTriangle, Send, FileText, ExternalLink,
  Car, Scale, Building2, Mail, Clock, ArrowDownLeft, ArrowUpRight, StickyNote,
  CheckCircle2, Scissors, XCircle, ChevronRight, Paperclip, User,
} from 'lucide-react';

// ============================================================
// Typen
// ============================================================

interface CockpitRow {
  ak: string;
  akte_id?: string | null;
  kunde_name?: string | null;
  fahrzeug_marke?: string | null;
  fahrzeug_modell?: string | null;
  kennzeichen?: string | null;
  versicherung?: string | null;
  versicherung_email?: string | null;
  anwalt_email?: string | null;
  schadennummer?: string | null;
  brutto?: number | string | null;
  offen?: number | string | null;
  rechnungs_nr?: string | null;
  rechnung_datum?: string | null;
  mahnstatus?: string | null;
  bezahlt?: number | string | null;
  letzte_zahlung?: string | null;
  status?: string | null;
  entscheidung?: string | null;
  betrag_gemindert?: number | string | null;
  grund?: string | null;
  status_am?: string | null;
  letzte_ein_datum?: string | null;
  letzte_ein_betreff?: string | null;
  letzte_ein_kategorie?: string | null;
  forderungsrelevant?: boolean | null;
  stellungnahme?: boolean | null;
  nachbesichtigung?: boolean | null;
  letzte_ein_von?: string | null;
  n_eingang?: number | string | null;
  n_dokumente?: number | string | null;
  empfohlene_aktion?: string | null;
}

interface VerlaufRow {
  ak: string;
  ts?: string | null;
  richtung?: 'raus' | 'ein' | 'notiz' | string | null;
  kanal?: string | null;
  titel?: string | null;
  von?: string | null;
  an?: string | null;
  text?: string | null;
  kategorie?: string | null;
  hat_anhang?: boolean | null;
}

interface DokumentRow {
  id: string;
  akte_id?: string | null;
  dateiname?: string | null;
  kategorie?: string | null;
  dateipfad?: string | null;
  erstellt_am?: string | null;
}

type ZielTyp = 'versicherung' | 'anwalt' | 'kunde';

// ============================================================
// Helfer
// ============================================================

/** Numerisches Feld defensiv parsen (String/number/null → number). */
function num(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** EUR de-DE formatieren. */
function eur(v: unknown): string {
  return num(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** Datum (ohne Zeit) de-DE. */
function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Datum + Uhrzeit de-DE. */
function fmtDateTime(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const AKTIONEN = [
  'Rückläufer prüfen / antworten',
  'Kürzung prüfen / Stellungnahme',
  'Stellungnahme (technische Kürzung)',
  'Abschließen: bezahlt',
  'Nachfassen (Versicherung/Anwalt)',
  'Beobachten',
] as const;

const DEFAULT_AKTION = 'Rückläufer prüfen / antworten';

/** Statuswerte für das manuelle Status-Dropdown. */
const STATUS_WERTE = [
  'offen', 'in_klaerung', 'rueckfrage', 'teilzahlung',
  'gekuerzt', 'ausgebucht', 'abgeschlossen', 'bezahlt',
] as const;

const STATUS_LABEL: Record<string, string> = {
  offen: 'Offen',
  in_klaerung: 'In Klärung',
  rueckfrage: 'Rückfrage',
  teilzahlung: 'Teilzahlung',
  gekuerzt: 'Gekürzt',
  ausgebucht: 'Ausgebucht',
  abgeschlossen: 'Abgeschlossen',
  bezahlt: 'Bezahlt',
};

function statusBadgeClasses(status?: string | null): string {
  switch ((status ?? '').toLowerCase()) {
    case 'bezahlt':
    case 'abgeschlossen':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'gekuerzt':
    case 'ausgebucht':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'rueckfrage':
    case 'in_klaerung':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'teilzahlung':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

/** Vorschlagstext für den Antwort-Composer je empfohlener Aktion. */
function vorschlagsText(row: CockpitRow): string {
  const anrede = 'Sehr geehrte Damen und Herren,';
  const gruss = '\n\nMit freundlichen Grüßen\nGutachter Koop';
  const az = `\n\nUnser Zeichen: ${row.ak ?? ''}${row.schadennummer ? ` · Schaden-Nr. ${row.schadennummer}` : ''}`;
  const offen = eur(row.offen);
  switch (row.empfohlene_aktion) {
    case 'Kürzung prüfen / Stellungnahme':
    case 'Stellungnahme (technische Kürzung)':
      return `${anrede}\n\nzu der von Ihnen vorgenommenen Kürzung nehmen wir wie folgt Stellung: Die abgerechneten Positionen sind sach- und fachgerecht ermittelt worden. Wir bitten um Ausgleich des noch offenen Betrages in Höhe von ${offen}.${az}${gruss}`;
    case 'Nachfassen (Versicherung/Anwalt)':
      return `${anrede}\n\nauf unsere Rechnung ist bislang kein vollständiger Ausgleich erfolgt. Der offene Betrag beträgt aktuell ${offen}. Wir bitten um kurzfristige Regulierung bzw. um einen Sachstand.${az}${gruss}`;
    case 'Rückläufer prüfen / antworten':
      return `${anrede}\n\nvielen Dank für Ihre Nachricht${row.letzte_ein_betreff ? ` ("${row.letzte_ein_betreff}")` : ''}. Wir nehmen wie folgt dazu Stellung und bitten um Regulierung des offenen Betrages in Höhe von ${offen}.${az}${gruss}`;
    default:
      return `${anrede}\n\n${az}${gruss}`;
  }
}

/** Standard-Betreff. */
function defaultBetreff(row: CockpitRow): string {
  return `Gutachter Koop · Vorgang ${row.ak ?? ''}${row.kunde_name ? ` · ${row.kunde_name}` : ''}`;
}

// ============================================================
// Kleine Präsentations-Komponenten
// ============================================================

function InfoZeile({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-sm text-gray-900 text-right break-words">{value ?? '—'}</span>
    </div>
  );
}

function Flag({ on, children }: { on?: boolean | null; children: React.ReactNode }) {
  if (!on) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#9B1C1C]/10 text-[#9B1C1C] text-xs font-medium px-2 py-0.5">
      <AlertTriangle className="w-3 h-3" /> {children}
    </span>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function ForderungsCockpitPage() {
  const [rows, setRows] = useState<CockpitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [aktivFilter, setAktivFilter] = useState<string>(DEFAULT_AKTION);
  const [suche, setSuche] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [selectedAk, setSelectedAk] = useState<string | null>(null);

  // Detail-Daten
  const [verlauf, setVerlauf] = useState<VerlaufRow[]>([]);
  const [dokumente, setDokumente] = useState<DokumentRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Composer
  const [ziel, setZiel] = useState<ZielTyp>('versicherung');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  // Entscheidungen
  const [mindernOpen, setMindernOpen] = useState(false);
  const [ausbuchenOpen, setAusbuchenOpen] = useState(false);
  const [minderBetrag, setMinderBetrag] = useState('');
  const [minderGrund, setMinderGrund] = useState('');
  const [ausbuchGrund, setAusbuchGrund] = useState('');
  const [manuellStatus, setManuellStatus] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // -------- Fallliste laden --------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('v_forderung_cockpit')
        .select('*');
      if (error) throw error;
      setRows((data as CockpitRow[]) ?? []);
    } catch (e: any) {
      console.error('[Forderungs-Cockpit] Laden der Fallliste fehlgeschlagen:', e);
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Aktueller Fall (aus rows)
  const selected = useMemo(
    () => rows.find((r) => r.ak === selectedAk) ?? null,
    [rows, selectedAk],
  );

  // -------- Detail (Verlauf + Dokumente) laden --------
  const loadDetail = useCallback(async (row: CockpitRow) => {
    setDetailLoading(true);
    setDetailError(null);
    setVerlauf([]);
    setDokumente([]);
    try {
      const [v, d] = await Promise.all([
        supabase.from('v_forderung_verlauf').select('*').eq('ak', row.ak),
        row.akte_id
          ? supabase.from('dokumente').select('*').eq('akte_id', row.akte_id)
          : Promise.resolve({ data: [], error: null } as any),
      ]);
      if (v.error) throw v.error;
      if (d && (d as any).error) throw (d as any).error;

      const vl = ((v.data as VerlaufRow[]) ?? []).slice().sort((a, b) => {
        const ta = a.ts ? new Date(a.ts).getTime() : 0;
        const tb = b.ts ? new Date(b.ts).getTime() : 0;
        return ta - tb; // chronologisch aufsteigend
      });
      setVerlauf(vl);

      const dl = (((d as any)?.data as DokumentRow[]) ?? []).slice().sort((a, b) => {
        const ta = a.erstellt_am ? new Date(a.erstellt_am).getTime() : 0;
        const tb = b.erstellt_am ? new Date(b.erstellt_am).getTime() : 0;
        return tb - ta; // jüngstes zuerst
      });
      setDokumente(dl);
    } catch (e: any) {
      console.error('[Forderungs-Cockpit] Detail-Laden fehlgeschlagen:', e);
      setDetailError(e?.message ?? String(e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Fall auswählen → Composer vorbelegen + Detail laden
  const selectFall = useCallback((row: CockpitRow) => {
    setSelectedAk(row.ak);
    setBetreff(defaultBetreff(row));
    setText(vorschlagsText(row));
    // Ziel sinnvoll vorbelegen: Versicherung wenn vorhanden, sonst Anwalt, sonst Kunde
    if (row.versicherung_email) setZiel('versicherung');
    else if (row.anwalt_email) setZiel('anwalt');
    else setZiel('kunde');
    setMindernOpen(false);
    setAusbuchenOpen(false);
    setMinderBetrag('');
    setMinderGrund('');
    setAusbuchGrund('');
    setManuellStatus(row.status ?? '');
    loadDetail(row);
  }, [loadDetail]);

  // -------- Filter + Suche --------
  const aktionCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) {
      const a = r.empfohlene_aktion ?? '—';
      m[a] = (m[a] ?? 0) + 1;
    }
    return m;
  }, [rows]);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return rows.filter((r) => {
      if (aktivFilter && r.empfohlene_aktion !== aktivFilter) return false;
      if (statusFilter && (r.status ?? '') !== statusFilter) return false;
      if (q) {
        const hay = [r.ak, r.kunde_name, r.versicherung].map((x) => (x ?? '').toString().toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, aktivFilter, statusFilter, suche]);

  // -------- Dokument öffnen (Signed URL) --------
  const openDokument = useCallback(async (dok: DokumentRow) => {
    if (!dok.dateipfad) {
      toast.error('Kein Dateipfad hinterlegt');
      return;
    }
    try {
      const { data, error } = await supabase.storage
        .from('dokumente')
        .createSignedUrl(dok.dateipfad, 3600);
      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Signierte URL konnte nicht erstellt werden');
      }
    } catch (e: any) {
      console.error('[Forderungs-Cockpit] Dokument öffnen fehlgeschlagen:', e);
      toast.error(e?.message ?? 'Dokument konnte nicht geöffnet werden');
    }
  }, []);

  // -------- Bearbeiter (E-Mail) --------
  const getBearbeiter = useCallback(async (): Promise<string> => {
    try {
      const { data } = await supabase.auth.getUser();
      return data?.user?.email ?? 'unbekannt';
    } catch {
      return 'unbekannt';
    }
  }, []);

  // -------- Entwurf erstellen (RPC) --------
  const erstelleEntwurf = useCallback(async () => {
    if (!selected) return;
    if (!betreff.trim() || !text.trim()) {
      toast.error('Betreff und Text dürfen nicht leer sein');
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.rpc('fn_forderung_reply_draft', {
        p_ak: selected.ak,
        p_ziel: ziel,
        p_betreff: betreff,
        p_text: text,
      });
      if (error) throw error;
      const res = data as { ok?: boolean; draft_id?: string; an?: string; error?: string } | null;
      if (res && res.ok) {
        toast.success(`Entwurf an ${res.an ?? ziel} erstellt – liegt unter E-Mail-Freigaben`);
      } else {
        toast.error(res?.error ?? 'Entwurf konnte nicht erstellt werden');
      }
    } catch (e: any) {
      console.error('[Forderungs-Cockpit] reply_draft fehlgeschlagen:', e);
      toast.error(e?.message ?? 'Entwurf konnte nicht erstellt werden');
    } finally {
      setSending(false);
    }
  }, [selected, ziel, betreff, text]);

  // -------- Status setzen (RPC) --------
  const setStatus = useCallback(async (opts: {
    status: string;
    entscheidung?: string | null;
    betrag_gemindert?: number | null;
    grund?: string | null;
  }) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const bearbeiter = await getBearbeiter();
      const { error } = await supabase.rpc('fn_forderung_set_status', {
        p_ak: selected.ak,
        p_status: opts.status,
        p_entscheidung: opts.entscheidung ?? null,
        p_betrag_gemindert: opts.betrag_gemindert ?? null,
        p_grund: opts.grund ?? null,
        p_bearbeiter: bearbeiter,
      });
      if (error) throw error;
      toast.success(`Status gesetzt: ${STATUS_LABEL[opts.status] ?? opts.status}`);
      setMindernOpen(false);
      setAusbuchenOpen(false);
      // Ansicht aktualisieren
      await load();
      await loadDetail(selected);
    } catch (e: any) {
      console.error('[Forderungs-Cockpit] set_status fehlgeschlagen:', e);
      toast.error(e?.message ?? 'Status konnte nicht gesetzt werden');
    } finally {
      setActionBusy(false);
    }
  }, [selected, getBearbeiter, load, loadDetail]);

  const handleMindern = useCallback(() => {
    const betrag = num(minderBetrag.replace(',', '.'));
    if (betrag <= 0) {
      toast.error('Bitte einen Minderungsbetrag > 0 angeben');
      return;
    }
    if (!minderGrund.trim()) {
      toast.error('Bitte einen Grund angeben');
      return;
    }
    setStatus({ status: 'gekuerzt', entscheidung: 'mindern', betrag_gemindert: betrag, grund: minderGrund.trim() });
  }, [minderBetrag, minderGrund, setStatus]);

  const handleAusbuchen = useCallback(() => {
    if (!ausbuchGrund.trim()) {
      toast.error('Bitte einen Grund für das Ausbuchen angeben');
      return;
    }
    setStatus({ status: 'ausgebucht', entscheidung: 'ausbuchen', grund: ausbuchGrund.trim() });
  }, [ausbuchGrund, setStatus]);

  // Verfügbare Ziele (E-Mail vorhanden?)
  const zielVerfuegbar: Record<ZielTyp, boolean> = {
    versicherung: !!selected?.versicherung_email,
    anwalt: !!selected?.anwalt_email,
    kunde: true, // Kunde serverseitig aufgelöst
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9B1C1C]/10 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-[#9B1C1C]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Forderungs-Cockpit</h1>
            <p className="text-sm text-gray-500">
              {loading ? 'Wird geladen…' : `${rows.length} Vorgänge · ${gefiltert.length} in Auswahl`}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#9B1C1C] hover:bg-[#7f1818] px-3 py-2 rounded-lg disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </button>
      </div>

      {/* Filterleiste */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {AKTIONEN.map((a) => {
            const count = aktionCounts[a] ?? 0;
            const active = aktivFilter === a;
            return (
              <button
                key={a}
                onClick={() => setAktivFilter(active ? '' : a)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'bg-[#9B1C1C] text-white border-[#9B1C1C]'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {a}
                <span className={`rounded-full px-1.5 ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Suche: AK, Kunde, Versicherung…"
              className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-72 focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30"
          >
            <option value="">Alle Status</option>
            {STATUS_WERTE.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Fehler-/Lade-/Leerzustand für die Liste */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Fehler beim Laden: {error}</span>
        </div>
      )}

      {/* Zwei-Spalten-Layout: Liste links, Detail rechts */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5 items-start">
        {/* ---- Fallliste ---- */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Fälle ({gefiltert.length})
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : gefiltert.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-gray-500">
                Keine Fälle in dieser Auswahl.
              </div>
            ) : (
              gefiltert.map((r) => {
                const active = r.ak === selectedAk;
                return (
                  <button
                    key={r.ak}
                    onClick={() => selectFall(r)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${active ? 'bg-[#9B1C1C]/5 border-l-2 border-[#9B1C1C]' : 'border-l-2 border-transparent'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{r.ak}</span>
                      <span className={`text-[11px] font-medium rounded-full border px-2 py-0.5 shrink-0 ${statusBadgeClasses(r.status)}`}>
                        {STATUS_LABEL[(r.status ?? '').toLowerCase()] ?? r.status ?? '—'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 truncate">{r.kunde_name ?? '—'}</div>
                    <div className="text-xs text-gray-500 truncate">{r.versicherung ?? '—'}</div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-sm font-medium text-[#9B1C1C]">{eur(r.offen)} offen</span>
                      <span className="text-[11px] text-gray-400">
                        {r.letzte_ein_datum ? `${fmtDate(r.letzte_ein_datum)}${r.letzte_ein_kategorie ? ` · ${r.letzte_ein_kategorie}` : ''}` : ''}
                      </span>
                    </div>
                    {r.empfohlene_aktion && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500">
                        <ChevronRight className="w-3 h-3" /> {r.empfohlene_aktion}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ---- Detailansicht ---- */}
        <div className="min-w-0">
          {!selected ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-20 text-center text-gray-500">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-gray-300" />
              Wähle links einen Fall aus, um Details, Verlauf, Dokumente und Aktionen zu sehen.
            </div>
          ) : (
            <div className="space-y-5">
              {/* a. Zusammenfassung / Wissen */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" /> {selected.kunde_name ?? '—'}
                    </h2>
                    <p className="text-sm text-gray-500">Vorgang {selected.ak}</p>
                  </div>
                  <span className={`text-xs font-medium rounded-full border px-3 py-1 ${statusBadgeClasses(selected.status)}`}>
                    {STATUS_LABEL[(selected.status ?? '').toLowerCase()] ?? selected.status ?? '—'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 mt-4">
                  <div>
                    <InfoZeile label="Fahrzeug" value={<span className="inline-flex items-center gap-1"><Car className="w-3.5 h-3.5 text-gray-400" />{[selected.fahrzeug_marke, selected.fahrzeug_modell].filter(Boolean).join(' ') || '—'}</span>} />
                    <InfoZeile label="Kennzeichen" value={selected.kennzeichen} />
                    <InfoZeile label="Versicherung" value={<span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-gray-400" />{selected.versicherung ?? '—'}</span>} />
                    <InfoZeile label="Schaden-Nr." value={selected.schadennummer} />
                    <InfoZeile label="Anwalt" value={selected.anwalt_email} />
                  </div>
                  <div>
                    <InfoZeile label="Brutto" value={eur(selected.brutto)} />
                    <InfoZeile label="Offen" value={<span className="font-semibold text-[#9B1C1C]">{eur(selected.offen)}</span>} />
                    <InfoZeile label="Bezahlt" value={eur(selected.bezahlt)} />
                    <InfoZeile label="Rechnung" value={`${selected.rechnungs_nr ?? '—'}${selected.rechnung_datum ? ` · ${fmtDate(selected.rechnung_datum)}` : ''}`} />
                    <InfoZeile label="Status seit" value={fmtDateTime(selected.status_am)} />
                  </div>
                </div>
              </section>

              {/* b. Was kam zurück */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <ArrowDownLeft className="w-4 h-4 text-[#9B1C1C]" /> Was kam zurück
                </h3>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{selected.letzte_ein_betreff ?? 'Kein Rückläufer erfasst'}</span>
                    <span className="text-xs text-gray-500">{fmtDate(selected.letzte_ein_datum)}{selected.letzte_ein_von ? ` · ${selected.letzte_ein_von}` : ''}</span>
                  </div>
                  {selected.letzte_ein_kategorie && (
                    <div className="mt-1 text-xs text-gray-500">Kategorie: {selected.letzte_ein_kategorie}</div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Flag on={selected.forderungsrelevant}>Forderungsrelevant</Flag>
                    <Flag on={selected.stellungnahme}>Stellungnahme nötig</Flag>
                    <Flag on={selected.nachbesichtigung}>Nachbesichtigung</Flag>
                  </div>
                </div>
              </section>

              {/* c. Verlauf */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-gray-400" /> Verlauf
                </h3>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : detailError ? (
                  <div className="text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{detailError}</div>
                ) : verlauf.length === 0 ? (
                  <div className="text-sm text-gray-500 py-4">Kein Verlauf vorhanden.</div>
                ) : (
                  <ol className="space-y-3">
                    {verlauf.map((v, i) => {
                      const isEin = v.richtung === 'ein';
                      const isNotiz = v.richtung === 'notiz';
                      const Icon = isEin ? ArrowDownLeft : isNotiz ? StickyNote : ArrowUpRight;
                      return (
                        <li
                          key={`${v.ak}-${i}-${v.ts ?? ''}`}
                          className={`rounded-lg border p-3 ${
                            isEin ? 'border-blue-200 bg-blue-50/60' : isNotiz ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5">
                              <Icon className={`w-3.5 h-3.5 ${isEin ? 'text-blue-600' : isNotiz ? 'text-amber-600' : 'text-gray-400'}`} />
                              {v.titel ?? (isEin ? 'Eingang' : isNotiz ? 'Notiz' : 'Ausgang')}
                              {v.hat_anhang && <Paperclip className="w-3 h-3 text-gray-400" />}
                            </span>
                            <span className="text-xs text-gray-500">{fmtDateTime(v.ts)}</span>
                          </div>
                          {(v.von || v.an) && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {v.von ? `von ${v.von}` : ''}{v.von && v.an ? ' · ' : ''}{v.an ? `an ${v.an}` : ''}
                              {v.kanal ? ` · ${v.kanal}` : ''}{v.kategorie ? ` · ${v.kategorie}` : ''}
                            </div>
                          )}
                          {v.text && (
                            <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap line-clamp-4">{v.text}</p>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>

              {/* d. Dokumente */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-gray-400" /> Dokumente {dokumente.length > 0 && <span className="text-gray-400">({dokumente.length})</span>}
                </h3>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-6 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : dokumente.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">Keine Dokumente vorhanden.</div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(
                      dokumente.reduce<Record<string, DokumentRow[]>>((acc, d) => {
                        const k = d.kategorie ?? 'Ohne Kategorie';
                        (acc[k] ||= []).push(d);
                        return acc;
                      }, {}),
                    ).map(([kat, docs]) => (
                      <div key={kat}>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{kat}</div>
                        <ul className="space-y-1">
                          {docs.map((d) => (
                            <li key={d.id}>
                              <button
                                onClick={() => openDokument(d)}
                                className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 hover:border-gray-300 hover:bg-gray-50 text-left"
                              >
                                <span className="text-sm text-gray-800 truncate inline-flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                  {d.dateiname ?? d.dateipfad ?? 'Dokument'}
                                </span>
                                <span className="text-xs text-gray-400 shrink-0 inline-flex items-center gap-1">
                                  {fmtDate(d.erstellt_am)} <ExternalLink className="w-3.5 h-3.5" />
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* e. Antworten */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-gray-400" /> Antwort-Entwurf
                </h3>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(['versicherung', 'anwalt', 'kunde'] as ZielTyp[]).map((z) => {
                      const verf = zielVerfuegbar[z];
                      const active = ziel === z;
                      return (
                        <button
                          key={z}
                          disabled={!verf}
                          onClick={() => setZiel(z)}
                          title={!verf ? 'Keine E-Mail-Adresse hinterlegt' : undefined}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            active ? 'bg-[#9B1C1C] text-white border-[#9B1C1C]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {z === 'versicherung' ? 'Versicherung' : z === 'anwalt' ? 'Anwalt' : 'Kunde'}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    value={betreff}
                    onChange={(e) => setBetreff(e.target.value)}
                    placeholder="Betreff"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30"
                  />
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={8}
                    placeholder="Text des Entwurfs"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 font-mono"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={erstelleEntwurf}
                      disabled={sending || (!zielVerfuegbar[ziel])}
                      className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#9B1C1C] hover:bg-[#7f1818] px-4 py-2 rounded-lg disabled:opacity-60"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Entwurf zur Freigabe erstellen
                    </button>
                  </div>
                </div>
              </section>

              {/* f. Entscheidungen */}
              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <Scale className="w-4 h-4 text-gray-400" /> Entscheidung
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setStatus({ status: 'abgeschlossen', entscheidung: 'abschliessen' })}
                    disabled={actionBusy}
                    className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-lg disabled:opacity-60"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Abschließen (bezahlt)
                  </button>
                  <button
                    onClick={() => { setMindernOpen((v) => !v); setAusbuchenOpen(false); }}
                    disabled={actionBusy}
                    className={`inline-flex items-center gap-2 text-sm font-medium border px-3 py-2 rounded-lg disabled:opacity-60 ${mindernOpen ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}
                  >
                    <Scissors className="w-4 h-4" /> Mindern
                  </button>
                  <button
                    onClick={() => { setAusbuchenOpen((v) => !v); setMindernOpen(false); }}
                    disabled={actionBusy}
                    className={`inline-flex items-center gap-2 text-sm font-medium border px-3 py-2 rounded-lg disabled:opacity-60 ${ausbuchenOpen ? 'bg-gray-200 border-gray-300 text-gray-800' : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'}`}
                  >
                    <XCircle className="w-4 h-4" /> Ausbuchen
                  </button>
                </div>

                {/* Mindern-Feld */}
                {mindernOpen && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <input
                        value={minderBetrag}
                        onChange={(e) => setMinderBetrag(e.target.value)}
                        inputMode="decimal"
                        placeholder="Minderungsbetrag (EUR)"
                        className="px-3 py-2 text-sm border border-amber-200 rounded-lg w-48 focus:outline-none focus:ring-2 focus:ring-amber-300"
                      />
                      <input
                        value={minderGrund}
                        onChange={(e) => setMinderGrund(e.target.value)}
                        placeholder="Grund der Minderung"
                        className="px-3 py-2 text-sm border border-amber-200 rounded-lg flex-1 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-amber-300"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleMindern}
                        disabled={actionBusy}
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-lg disabled:opacity-60"
                      >
                        {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />}
                        Minderung speichern
                      </button>
                    </div>
                  </div>
                )}

                {/* Ausbuchen-Feld */}
                {ausbuchenOpen && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <input
                      value={ausbuchGrund}
                      onChange={(e) => setAusbuchGrund(e.target.value)}
                      placeholder="Grund für das Ausbuchen"
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleAusbuchen}
                        disabled={actionBusy}
                        className="inline-flex items-center gap-2 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 px-3 py-2 rounded-lg disabled:opacity-60"
                      >
                        {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Ausbuchen bestätigen
                      </button>
                    </div>
                  </div>
                )}

                {/* Manuelles Status-Dropdown */}
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                  <span className="text-xs text-gray-500">Status manuell setzen:</span>
                  <select
                    value={manuellStatus}
                    onChange={(e) => setManuellStatus(e.target.value)}
                    className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30"
                  >
                    <option value="">— wählen —</option>
                    {STATUS_WERTE.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => manuellStatus && setStatus({ status: manuellStatus })}
                    disabled={actionBusy || !manuellStatus}
                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-lg disabled:opacity-50"
                  >
                    Anwenden
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
